import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';
import { Dayjs } from 'dayjs';

import AuditLogContext from '@totem/components/services/remoteAccess/auditLog/AuditLogContext';
import {
  AuditLogEntry,
  AuditLogPaging,
  AuditLogResults,
} from '@totem/components/services/remoteAccess/auditLog/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { REMOTE_ACCESS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children?: ReactNode;
};

const DEFAULT_PAGE_SIZE = 50;

const AuditLogContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [data, setData] = useState<AuditLogEntry[]>([]);
  const [paging, setPaging] = useState<AuditLogPaging>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalRecords: 0,
  });
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [userSearch, setUserSearch] = useState<string>('');
  const [assigneeSearch, setAssigneeSearch] = useState<string>('');
  const [roleSearch, setRoleSearch] = useState<string>('');

  useEffect(() => {
    if (!refreshData || isSending) {
      return;
    }

    setRefreshData(false);

    setIsSending(true);

    setLoading(true);

    const queryParams = new URLSearchParams();
    queryParams.set('page', String(paging.page));

    queryParams.set('pageSize', String(paging.pageSize));
    if (startDate) {
      queryParams.set('startDate', startDate.startOf('day').toISOString());
    }
    if (endDate) {
      queryParams.set('endDate', endDate.endOf('day').toISOString());
    }
    if (userSearch) {
      queryParams.set('user', userSearch);
    }
    if (assigneeSearch) {
      queryParams.set('assignee', assigneeSearch);
    }
    if (roleSearch) {
      queryParams.set('role', roleSearch);
    }

    fetch(`${REMOTE_ACCESS_ENDPOINT}/audit/log?${queryParams.toString()}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          throw new Error('Failed to load audit log.');
        }
        return res.json();
      })
      .then((result: AuditLogResults) => {
        setData(Array.isArray(result.data) ? result.data : []);

        setPaging((prev) => ({
          ...prev,
          totalRecords: result.paging?.totalRecords ?? 0,
        }));
      })
      .catch(() => {
        notification.error({
          message: 'Error loading audit log',
          description:
            'An error occurred while loading the remote access audit log.',
          duration: 0,
        });
      })
      .finally(() => {
        setLoading(false);

        setIsSending(false);
      });
  }, [
    refreshData,
    isSending,
    paging.page,
    paging.pageSize,
    startDate,
    endDate,
    userSearch,
    assigneeSearch,
    roleSearch,
    getApiToken,
  ]);

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  const resetPage = useCallback(() => {
    setPaging((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshData(true);
  }, []);

  const handleStartDateChange = useCallback(
    (date: Dayjs | null) => {
      setStartDate(date);

      resetPage();

      setRefreshData(true);
    },
    [resetPage],
  );

  const handleEndDateChange = useCallback(
    (date: Dayjs | null) => {
      setEndDate(date);

      resetPage();

      setRefreshData(true);
    },
    [resetPage],
  );

  const handleUserSearchChange = useCallback(
    (value: string) => {
      setUserSearch(value);

      resetPage();

      setRefreshData(true);
    },
    [resetPage],
  );

  const handleAssigneeSearchChange = useCallback(
    (value: string) => {
      setAssigneeSearch(value);

      resetPage();

      setRefreshData(true);
    },
    [resetPage],
  );

  const handleRoleSearchChange = useCallback(
    (value: string) => {
      setRoleSearch(value);

      resetPage();

      setRefreshData(true);
    },
    [resetPage],
  );

  const handlePageChange = useCallback((page: number, pageSize: number) => {
    setPaging((prev) => ({ ...prev, page, pageSize }));

    setRefreshData(true);
  }, []);

  return (
    <AuditLogContext.Provider
      value={{
        loading,
        data,
        paging,
        startDate,
        endDate,
        userSearch,
        assigneeSearch,
        roleSearch,
        onStartDateChange: handleStartDateChange,
        onEndDateChange: handleEndDateChange,
        onUserSearchChange: handleUserSearchChange,
        onAssigneeSearchChange: handleAssigneeSearchChange,
        onRoleSearchChange: handleRoleSearchChange,
        onPageChange: handlePageChange,
        onRefresh: handleRefresh,
      }}
    >
      {children}
    </AuditLogContext.Provider>
  );
};

export default AuditLogContainer;
